import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation from '../components/DeveloperNavigation';
import { optionalAuthHeaders } from '../utils/billingApi';
import { getBackendBaseUrl } from '../utils/apiUrls';

const BACKEND_ROOT = getBackendBaseUrl();
const API_BASE = process.env.REACT_APP_SIGNATURE_EVAL_URL || `${BACKEND_ROOT}/signature-eval`;

function groundTruthBlockAliases(block) {
  const role = block.role || '';
  const blockType = block.block_type || '';
  const page = block.page;
  const aliases = [];
  if (block.block_id) aliases.push(block.block_id);
  if (role && blockType === 'signature') aliases.push(`${role}_signature`);
  if (role && blockType === 'initials' && page) aliases.push(`${role}_initials_p${page}`);
  if (role && blockType && page) aliases.push(`${role}_${blockType}_p${page}`);
  return [...new Set(aliases.filter(Boolean))];
}

function resolveVerdictForBlock(block, verdictMap, gtBlocks = []) {
  for (const id of groundTruthBlockAliases(block)) {
    if (verdictMap[id]) {
      return { ...verdictMap[id], block_id: block.block_id };
    }
    const gt = gtBlocks.find((b) => b.block_id === id);
    if (gt) {
      return { ...gt, block_id: block.block_id };
    }
  }
  return null;
}

function resolveMissingFlagForBlock(block, missingMap) {
  for (const id of groundTruthBlockAliases(block)) {
    if (missingMap[id]) return true;
  }
  return false;
}

function rebuildVerdictsAfterDetect(prevVerdicts, prevMissing, newBlocks, gtBlocks) {
  const merged = {};
  const missingIds = {};
  newBlocks.forEach((block) => {
    const bid = block.block_id;
    const verdict = resolveVerdictForBlock(block, prevVerdicts, gtBlocks);
    if (verdict) merged[bid] = verdict;
    if (
      verdict?.verdict_source === 'missing_from_keyflo'
      || resolveMissingFlagForBlock(block, prevMissing)
    ) {
      missingIds[bid] = true;
    }
  });
  return { merged, missingIds };
}

const PDFJS_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const PDFJS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

function loadPdfJs() {
  return new Promise((resolve, reject) => {
    if (window.pdfjsLib) {
      resolve(window.pdfjsLib);
      return;
    }
    const script = document.createElement('script');
    script.src = PDFJS_CDN;
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
      resolve(window.pdfjsLib);
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

function PdfPageViewer({ pdfUrl, pageNumber, bboxHint, pdfHeaders, showBbox = true, flipBBoxY = false, onPageRendered }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!pdfUrl) {
      setPdfDoc(null);
      return undefined;
    }

    (async () => {
      try {
        const pdfjs = await loadPdfJs();
        const doc = await pdfjs.getDocument({
          url: pdfUrl,
          httpHeaders: pdfHeaders || {},
        }).promise;
        if (!cancelled) {
          setPdfDoc(doc);
          setError('');
        }
      } catch (e) {
        if (!cancelled) {
          setPdfDoc(null);
          setError(String(e.message || e));
        }
      }
    })();

    return () => { cancelled = true; };
  }, [pdfUrl, pdfHeaders]);

  useEffect(() => {
    let cancelled = false;
    if (!pdfDoc || !pageNumber) return undefined;

    (async () => {
      try {
        const page = await pdfDoc.getPage(pageNumber);
        const containerWidth = containerRef.current?.clientWidth || 600;
        const viewport = page.getViewport({ scale: 1 });
        const scale = containerWidth / viewport.width;
        const scaled = page.getViewport({ scale });
        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;
        const ctx = canvas.getContext('2d');
        canvas.width = scaled.width;
        canvas.height = scaled.height;
        setPageSize({ width: scaled.width, height: scaled.height });
        await page.render({ canvasContext: ctx, viewport: scaled }).promise;
        if (!cancelled && onPageRendered) {
          onPageRendered(canvas);
        }
      } catch (e) {
        if (!cancelled) setError(String(e.message || e));
      }
    })();

    return () => { cancelled = true; };
  }, [pdfDoc, pageNumber, onPageRendered]);

  const overlay = useMemo(() => {
    if (!bboxHint || !pageSize.width || !showBbox) return null;
    const x = bboxHint.x_pct || 0;
    const y = bboxHint.y_pct || 0;
    const w = bboxHint.w_pct || 0.2;
    const h = bboxHint.h_pct || 0.04;
    const topPct = flipBBoxY ? (1 - y - h) : y;
    return {
      left: `${x * 100}%`,
      top: `${topPct * 100}%`,
      width: `${w * 100}%`,
      height: `${h * 100}%`,
    };
  }, [bboxHint, pageSize, showBbox, flipBBoxY]);

  if (error) {
    return <div style={{ color: '#b91c1c', padding: 12 }}>PDF error: {error}</div>;
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: 'auto' }} />
      {overlay && (
        <div
          style={{
            position: 'absolute',
            ...overlay,
            border: '3px solid #f59e0b',
            background: 'rgba(245, 158, 11, 0.15)',
            boxSizing: 'border-box',
            pointerEvents: 'none',
            animation: 'sigEvalPulse 1.5s ease-in-out infinite',
          }}
        />
      )}
      <style>{`
        @keyframes sigEvalPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.5); }
          50% { box-shadow: 0 0 0 6px rgba(245, 158, 11, 0); }
        }
      `}</style>
    </div>
  );
}

function BboxCropPreview({ pageRender, bboxHint, flipBBoxY = false }) {
  const cropRef = useRef(null);
  const pageReady = Boolean(pageRender?.canvas);

  useEffect(() => {
    const cropCanvas = cropRef.current;
    const sourceCanvas = pageRender?.canvas;
    if (!cropCanvas || !sourceCanvas || !bboxHint) return;

    const sw = sourceCanvas.width;
    const sh = sourceCanvas.height;
    const x = Math.max(0, Math.floor((bboxHint.x_pct || 0) * sw));
    const w = Math.max(1, Math.floor((bboxHint.w_pct || 0.2) * sw));
    const h = Math.max(1, Math.floor((bboxHint.h_pct || 0.04) * sh));
    const yRaw = (bboxHint.y_pct || 0) * sh;
    const y = flipBBoxY
      ? Math.max(0, Math.floor(sh - yRaw - h))
      : Math.max(0, Math.floor(yRaw));

    const ctx = cropCanvas.getContext('2d');
    cropCanvas.width = w;
    cropCanvas.height = h;
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(sourceCanvas, x, y, w, h, 0, 0, w, h);
  }, [pageRender, bboxHint, flipBBoxY]);

  if (!bboxHint) return null;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#475569' }}>
        Detected region zoom
      </div>
      {!pageReady ? (
        <div
          style={{
            padding: '20px 16px',
            border: '2px dashed #cbd5e1',
            borderRadius: 4,
            background: '#f8fafc',
            color: '#64748b',
            fontSize: 12,
          }}
        >
          Loading page render for crop preview…
        </div>
      ) : null}
      <canvas
        ref={cropRef}
        style={{
          display: pageReady ? 'block' : 'none',
          maxWidth: '100%',
          border: '2px solid #f59e0b',
          borderRadius: 4,
          background: '#fff',
        }}
      />
      <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
        If this crop is blank or wrong, try the reference page toggle or Flip Y (debug) below.
      </div>
    </div>
  );
}

function DeveloperSignatureEval() {
  const { user, isAuthenticated, loginWithRedirect, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const navigate = useNavigate();

  const [queue, setQueue] = useState([]);
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [blockIndex, setBlockIndex] = useState(0);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfHeaders, setPdfHeaders] = useState({});
  const [loading, setLoading] = useState(false);
  const [detectingBlocks, setDetectingBlocks] = useState(false);
  const [error, setError] = useState('');
  const [verdicts, setVerdicts] = useState({});
  const [reviewStatus, setReviewStatus] = useState('pending');
  const [flagAmbiguous, setFlagAmbiguous] = useState(false);
  const [missingBlockIds, setMissingBlockIds] = useState({});
  const [pageViewMode, setPageViewMode] = useState('detected');
  const [flipBBoxY, setFlipBBoxY] = useState(false);
  const [pageRender, setPageRender] = useState(null);
  const [savingCase, setSavingCase] = useState(false);
  const [memberIndex, setMemberIndex] = useState(0);
  const selectedCaseIdRef = useRef(null);
  const detectRequestIdRef = useRef(0);
  const saveInFlightRef = useRef(false);
  const pageRenderVersionRef = useRef(0);

  const isKeyFloUser = useMemo(() => {
    const email = user?.email || '';
    return ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'].includes(email);
  }, [user]);

  const callerEmail = user?.email || '';

  const authConfig = useCallback(async () => {
    const headers = await optionalAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
    return { headers, params: { caller_email: callerEmail } };
  }, [callerEmail, getAccessTokenSilently, getIdTokenClaims]);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { headers, params } = await authConfig();
      const res = await axios.get(`${API_BASE}/queue`, {
        headers,
        params: { ...params, status: 'pending', limit: 100 },
      });
      setQueue(res.data.cases || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [authConfig]);

  const detectBlocks = useCallback(async (
    caseId,
    {
      force = false,
      gtBlocks = null,
      missingSnapshot = null,
      verdictsSnapshot = null,
    } = {},
  ) => {
    const requestId = ++detectRequestIdRef.current;
    setDetectingBlocks(true);
    setError('');
    try {
      const { headers } = await authConfig();
      const res = await axios.post(`${API_BASE}/cases/${caseId}/detect-blocks`, {
        caller_email: callerEmail,
        force,
      }, { headers });
      // Ignore stale responses if the auditor switched cases mid-detect.
      if (
        requestId !== detectRequestIdRef.current
        || selectedCaseIdRef.current !== caseId
      ) {
        return null;
      }
      const newBlocks = res.data.blocks || [];
      setBlocks(newBlocks);
      const resolvedGtBlocks = gtBlocks || (caseData?.ground_truth || {}).blocks || [];
      const prevMissing = missingSnapshot ?? {};
      const prevVerdicts = verdictsSnapshot ?? {};
      const rebuilt = rebuildVerdictsAfterDetect(
        prevVerdicts,
        prevMissing,
        newBlocks,
        resolvedGtBlocks,
      );
      setVerdicts(rebuilt.merged);
      setMissingBlockIds(rebuilt.missingIds);
      if (res.data.observed_blocks) {
        setCaseData((prev) => (
          prev && selectedCaseIdRef.current === caseId
            ? { ...prev, observed_blocks: res.data.observed_blocks }
            : prev
        ));
      }
      setBlockIndex(0);
      setPageViewMode('detected');
      return res.data;
    } catch (e) {
      if (
        requestId === detectRequestIdRef.current
        && selectedCaseIdRef.current === caseId
      ) {
        setError(e.response?.data?.error || e.message);
      }
      return null;
    } finally {
      if (requestId === detectRequestIdRef.current) {
        setDetectingBlocks(false);
      }
    }
  }, [authConfig, callerEmail, caseData]);

  const loadCase = useCallback(async (caseId, { autoDetect = true } = {}) => {
    ++detectRequestIdRef.current;
    selectedCaseIdRef.current = caseId;
    setSelectedCaseId(caseId);
    // Clear previous case immediately so Save/verdict actions cannot write
    // the old case's blocks into the newly selected case id.
    setCaseData(null);
    setBlocks([]);
    setBlockIndex(0);
    setVerdicts({});
    setMissingBlockIds({});
    setPdfUrl(null);
    setPageRender(null);
    setLoading(true);
    setError('');
    setDetectingBlocks(false);
    try {
      const { headers, params } = await authConfig();
      const res = await axios.get(`${API_BASE}/cases/${caseId}`, {
        headers,
        params,
      });
      if (selectedCaseIdRef.current !== caseId) {
        return;
      }
      const c = res.data.case;
      setCaseData(c);
      setPdfHeaders(headers);
      setPdfUrl(`${API_BASE}/cases/${caseId}/pdf?caller_email=${encodeURIComponent(callerEmail)}`);
      setReviewStatus(c.review_status || 'pending');
      setFlagAmbiguous(c.review_status === 'ambiguous');
      setPageViewMode('detected');
      setFlipBBoxY(false);

      const gtBlocks = (c.ground_truth || {}).blocks || [];
      const initialBlocks = res.data.blocks || [];
      const existing = {};
      const missingIds = {};
      initialBlocks.forEach((block) => {
        const verdict = resolveVerdictForBlock(block, {}, gtBlocks);
        if (!verdict) return;
        existing[block.block_id] = verdict;
        if (verdict.verdict_source === 'missing_from_keyflo') {
          missingIds[block.block_id] = true;
        }
      });
      setVerdicts(existing);
      setMissingBlockIds(missingIds);
      setBlocks(initialBlocks);
      setBlockIndex(0);

      if (autoDetect && !c.observed_blocks) {
        // Keep loading=true so labeling stays disabled until detect finishes.
        await detectBlocks(caseId, {
          force: false,
          gtBlocks,
          missingSnapshot: missingIds,
          verdictsSnapshot: existing,
        });
      }
    } catch (e) {
      if (selectedCaseIdRef.current === caseId) {
        setError(e.response?.data?.error || e.message);
        selectedCaseIdRef.current = null;
        setSelectedCaseId(null);
        setCaseData(null);
        setBlocks([]);
        setLoading(false);
      }
    } finally {
      if (selectedCaseIdRef.current === caseId) {
        setLoading(false);
      }
    }
  }, [authConfig, callerEmail, detectBlocks]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isKeyFloUser) navigate('/');
  }, [isAuthenticated, isKeyFloUser, navigate]);

  useEffect(() => {
    if (isAuthenticated && isKeyFloUser) fetchQueue();
  }, [isAuthenticated, isKeyFloUser, fetchQueue]);

  const currentBlock = blocks[blockIndex] || null;

  const buildGroundTruthPayload = useCallback((verdictsSnapshot, missingSnapshot) => {
    const activeBlockIds = new Set(blocks.map((b) => b.block_id));
    const gtBlocksMap = {};
    Object.entries(verdictsSnapshot).forEach(([bid, verdict]) => {
      if (activeBlockIds.has(bid)) {
        gtBlocksMap[bid] = verdict;
      }
    });
    Object.keys(missingSnapshot).forEach((bid) => {
      if (!activeBlockIds.has(bid) || !missingSnapshot[bid]) return;
      gtBlocksMap[bid] = {
        block_id: bid,
        status: verdictsSnapshot[bid]?.status || gtBlocksMap[bid]?.status || 'unsigned',
        verdict_source: 'missing_from_keyflo',
        keyflo_evaluated: false,
        notes: 'KeyFlo missed this block',
      };
    });
    return gtBlocksMap;
  }, [blocks]);

  const saveCase = useCallback(async (statusOverride, options = {}) => {
    if (!selectedCaseId || !caseData) return false;
    if (saveInFlightRef.current) return false;
    saveInFlightRef.current = true;
    setSavingCase(true);
    const verdictsSnapshot = options.verdictsSnapshot || verdicts;
    const missingSnapshot = options.missingSnapshot || missingBlockIds;
    setLoading(true);
    const gtBlocksMap = buildGroundTruthPayload(verdictsSnapshot, missingSnapshot);
    const finalStatus = statusOverride || (flagAmbiguous ? 'ambiguous' : 'verified');
    const unlabeled = blocks.filter((b) => !gtBlocksMap[b.block_id]);
    if (finalStatus === 'verified' && unlabeled.length > 0) {
      setError(`Label all ${blocks.length} blocks before marking verified (${unlabeled.length} remaining).`);
      setLoading(false);
      saveInFlightRef.current = false;
      setSavingCase(false);
      return false;
    }
    const gtBlocks = Object.values(gtBlocksMap);
    const groundTruth = { blocks: gtBlocks };
    if (caseData?.ground_truth?.fully_executed !== undefined) {
      groundTruth.fully_executed = caseData.ground_truth.fully_executed;
    } else if (caseData?.keyflo_prediction?.fully_executed !== undefined) {
      groundTruth.fully_executed = caseData.keyflo_prediction.fully_executed;
    }
    const auditorConfidence = options.auditorConfidence
      ?? (finalStatus === 'ambiguous' ? 'low' : 'high');
    const caseIdBeingSaved = selectedCaseId;
    try {
      const { headers } = await authConfig();
      await axios.post(`${API_BASE}/cases/${caseIdBeingSaved}/label`, {
        caller_email: callerEmail,
        ground_truth: groundTruth,
        review_status: finalStatus,
        metadata: { auditor_confidence: auditorConfidence },
      }, { headers });
      const queueRes = await axios.get(`${API_BASE}/queue`, {
        headers,
        params: { caller_email: callerEmail, status: 'pending', limit: 100 },
      });
      const nextQueue = queueRes.data.cases || [];
      setQueue(nextQueue);
      if (selectedCaseIdRef.current === caseIdBeingSaved) {
        setReviewStatus(finalStatus);
      }
      if (options.advanceToNext && selectedCaseIdRef.current === caseIdBeingSaved) {
        const nextCase = nextQueue.find((c) => c.case_id !== caseIdBeingSaved);
        if (nextCase) {
          await loadCase(nextCase.case_id);
        } else {
          selectedCaseIdRef.current = null;
          setCaseData(null);
          setSelectedCaseId(null);
          setBlocks([]);
          setBlockIndex(0);
        }
      }
      return true;
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      return false;
    } finally {
      saveInFlightRef.current = false;
      setSavingCase(false);
      setLoading(false);
    }
  }, [
    selectedCaseId,
    verdicts,
    missingBlockIds,
    buildGroundTruthPayload,
    blocks,
    flagAmbiguous,
    caseData,
    authConfig,
    callerEmail,
    loadCase,
  ]);

  const applyVerdict = async (action) => {
    if (!currentBlock || savingCase || saveInFlightRef.current || loading || detectingBlocks) return;
    const bid = currentBlock.block_id;
    let status = currentBlock.keyflo_status;
    let verdictSource = 'confirm_keyflo';

    if (action === 'confirm_keyflo') {
      status = currentBlock.keyflo_status;
      verdictSource = 'confirm_keyflo';
    } else if (action === 'confirm_oracle') {
      status = currentBlock.oracle_status;
      verdictSource = 'confirm_oracle';
    } else if (action === 'reject_unsigned') {
      status = 'signed';
      verdictSource = 'reject';
    } else if (action === 'reject_signed') {
      status = 'unsigned';
      verdictSource = 'reject';
    }

    // Auditor only confirms signed vs unsigned — never expectedness.
    if (status !== 'signed' && status !== 'unsigned') return;

    const nextVerdicts = {
      ...verdicts,
      [bid]: {
        block_id: bid,
        status,
        verdict_source: verdictSource,
        keyflo_evaluated: currentBlock.keyflo_evaluated !== false,
        notes: '',
      },
    };
    const nextMissing = { ...missingBlockIds };
    delete nextMissing[bid];

    setVerdicts(nextVerdicts);
    setMissingBlockIds(nextMissing);

    const isLastBlock = blockIndex >= blocks.length - 1;
    if (!isLastBlock) {
      setBlockIndex(blockIndex + 1);
      setPageViewMode('detected');
      return;
    }

    await saveCase('verified', {
      verdictsSnapshot: nextVerdicts,
      missingSnapshot: nextMissing,
      advanceToNext: true,
    });
  };

  const confirmAll = () => {
    const next = { ...verdicts };
    blocks.forEach((b) => {
      if (next[b.block_id]) return;
      const raw = b.disagreement && b.oracle_status ? b.oracle_status : b.keyflo_status;
      if (raw !== 'signed' && raw !== 'unsigned') return;
      next[b.block_id] = {
        block_id: b.block_id,
        status: raw,
        verdict_source: b.disagreement ? 'confirm_oracle' : 'confirm_keyflo',
        keyflo_evaluated: b.keyflo_evaluated !== false,
      };
    });
    setVerdicts(next);
  };

  const runOracle = async () => {
    if (!selectedCaseId) return;
    setLoading(true);
    try {
      const { headers } = await authConfig();
      await axios.post(`${API_BASE}/cases/${selectedCaseId}/run-oracle`, {
        caller_email: callerEmail,
      }, { headers });
      await loadCase(selectedCaseId, { autoDetect: false });
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const memberBlocks = currentBlock?.member_blocks || [];
  const activeMember = memberBlocks.length
    ? memberBlocks[Math.min(memberIndex, memberBlocks.length - 1)]
    : null;
  const detectedPage = activeMember?.page || currentBlock?.page || 1;
  const activeBboxHint = activeMember?.bbox_hint || currentBlock?.bbox_hint || null;
  const referencePage = currentBlock?.reference_page || null;
  const viewerPage = (
    pageViewMode === 'reference' && referencePage
  ) ? referencePage : detectedPage;
  const showBboxOverlay = Boolean(
    activeBboxHint
    && pageViewMode === 'detected'
    && viewerPage === detectedPage
  );

  const handlePageRendered = useCallback((canvas) => {
    // Same canvas DOM node is reused across pages; bump version so crop preview refreshes.
    pageRenderVersionRef.current += 1;
    setPageRender({ canvas, version: pageRenderVersionRef.current });
  }, []);

  useEffect(() => {
    setPageViewMode('detected');
    setMemberIndex(0);
  }, [blockIndex, selectedCaseId]);

  useEffect(() => {
    setPageRender(null);
  }, [pageViewMode, viewerPage, selectedCaseId]);

  if (!isAuthenticated) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <p>Please log in to access Signature Eval.</p>
        <button type="button" onClick={() => loginWithRedirect()}>Log In</button>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 24px', maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>Signature Eval</h1>
        <DeveloperNavigation />
      </div>

      {error && (
        <div style={{ background: '#fef2f2', color: '#b91c1c', padding: 12, borderRadius: 6, marginBottom: 12 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16 }}>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, maxHeight: '80vh', overflow: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <strong>Queue ({queue.length})</strong>
            <button type="button" onClick={fetchQueue}>Refresh</button>
          </div>
          {loading && !caseData && <LoadingSpinner />}
          {queue.map((c) => (
            <button
              key={c.case_id}
              type="button"
              disabled={savingCase || loading}
              onClick={() => loadCase(c.case_id)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '8px 10px',
                marginBottom: 4,
                border: selectedCaseId === c.case_id ? '2px solid #2563eb' : '1px solid #e5e7eb',
                borderRadius: 4,
                background: selectedCaseId === c.case_id ? '#eff6ff' : '#fff',
                cursor: (savingCase || loading) ? 'not-allowed' : 'pointer',
                opacity: (savingCase || loading) ? 0.6 : 1,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600 }}>{c.case_id}</div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>{c.doc_file_type}</div>
              {c.disagreement_count > 0 && (
                <div style={{ fontSize: 11, color: '#d97706' }}>{c.disagreement_count} disagreements</div>
              )}
            </button>
          ))}
        </div>

        {caseData ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 16 }}>
            <div>
              <div style={{ marginBottom: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" disabled={savingCase || loading || detectingBlocks} onClick={() => saveCase('verified')}>Save</button>
                <button type="button" disabled={savingCase || loading || detectingBlocks} onClick={() => saveCase('skipped')}>Skip</button>
                <button
                  type="button"
                  disabled={savingCase || loading || detectingBlocks}
                  onClick={() => saveCase('ambiguous', { auditorConfidence: 'low' })}
                >
                  Flag ambiguous
                </button>
                <button type="button" disabled={savingCase || loading || detectingBlocks} onClick={confirmAll}>Confirm all</button>
                <button type="button" disabled={savingCase || loading || detectingBlocks} onClick={runOracle}>Run oracle</button>
                <button
                  type="button"
                  disabled={detectingBlocks || savingCase || loading || !selectedCaseId}
                  onClick={() => detectBlocks(selectedCaseId, {
                    force: Boolean(caseData?.observed_blocks),
                    gtBlocks: (caseData?.ground_truth || {}).blocks || [],
                    missingSnapshot: missingBlockIds,
                    verdictsSnapshot: verdicts,
                  })}
                >
                  {detectingBlocks
                    ? 'Detecting blocks…'
                    : (caseData?.observed_blocks ? 'Re-detect blocks' : 'Detect blocks')}
                </button>
                <span style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>
                  Status: {reviewStatus}
                  {caseData?.observed_blocks?.run_at && (
                    <> · Blocks detected {new Date(caseData.observed_blocks.run_at).toLocaleString()}</>
                  )}
                </span>
              </div>

              {currentBlock && (
                <>
                  <div style={{ fontSize: 14, marginBottom: 8 }}>
                    Block {blockIndex + 1} / {blocks.length}: {currentBlock.label}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                    <div>Block ID: {currentBlock.block_id}</div>
                    <div>
                      Detected page: {detectedPage}
                      {referencePage ? ` · KeyFlo evidence page: ${referencePage}` : ''}
                      {currentBlock.page_mismatch && (
                        <span style={{ color: '#d97706', marginLeft: 6 }}>⚠ page mismatch</span>
                      )}
                    </div>
                    {currentBlock.block_type === 'signature' && (
                      <div>
                        Expected parties: {currentBlock.expected_party_count ?? '—'}
                        {' · '}Detected lines: {currentBlock.observed_line_count ?? 1}
                        {currentBlock.count_shortfall && (
                          <span style={{ color: '#d97706', marginLeft: 6 }}>
                            ⚠ fewer lines than expected parties (often unsigned)
                          </span>
                        )}
                      </div>
                    )}
                    {activeBboxHint && (
                      <div>
                        BBox: x={(activeBboxHint.x_pct * 100).toFixed(1)}%
                        {' '}y={(activeBboxHint.y_pct * 100).toFixed(1)}%
                        {' '}w={(activeBboxHint.w_pct * 100).toFixed(1)}%
                        {' '}h={(activeBboxHint.h_pct * 100).toFixed(1)}%
                      </div>
                    )}
                  </div>
                  {memberBlocks.length > 1 && (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, color: '#64748b' }}>
                        Signature line {memberIndex + 1} / {memberBlocks.length}
                        {activeMember?.label ? `: ${activeMember.label}` : ''}
                      </span>
                      <button
                        type="button"
                        disabled={memberIndex <= 0}
                        onClick={() => setMemberIndex((i) => Math.max(0, i - 1))}
                      >
                        Prev line
                      </button>
                      <button
                        type="button"
                        disabled={memberIndex >= memberBlocks.length - 1}
                        onClick={() => setMemberIndex((i) => Math.min(memberBlocks.length - 1, i + 1))}
                      >
                        Next line
                      </button>
                    </div>
                  )}
                  {currentBlock.page_mismatch && referencePage && (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => setPageViewMode('detected')}
                        style={{ fontWeight: pageViewMode === 'detected' ? 700 : 400 }}
                      >
                        Detected page ({detectedPage}) + highlight
                      </button>
                      <button
                        type="button"
                        onClick={() => setPageViewMode('reference')}
                        style={{ fontWeight: pageViewMode === 'reference' ? 700 : 400 }}
                      >
                        KeyFlo page ({referencePage})
                      </button>
                    </div>
                  )}
                  {pageViewMode === 'reference' && currentBlock.page_mismatch && (
                    <div style={{ fontSize: 12, color: '#b45309', marginBottom: 8 }}>
                      Showing KeyFlo evidence page — bbox highlight hidden because detection assigned page {detectedPage}.
                    </div>
                  )}
                  <label style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <input
                      type="checkbox"
                      checked={flipBBoxY}
                      onChange={(e) => setFlipBBoxY(e.target.checked)}
                    />
                    Flip Y (debug coordinate mapping)
                  </label>
                  {detectingBlocks && (
                    <div style={{ marginBottom: 8, color: '#6b7280' }}>
                      <LoadingSpinner /> Detecting signature blocks from PDF…
                    </div>
                  )}
                  <PdfPageViewer
                    pdfUrl={pdfUrl}
                    pdfHeaders={pdfHeaders}
                    pageNumber={viewerPage}
                    bboxHint={activeBboxHint}
                    showBbox={showBboxOverlay}
                    flipBBoxY={flipBBoxY}
                    onPageRendered={handlePageRendered}
                  />
                  {showBboxOverlay && activeBboxHint && (
                    <BboxCropPreview
                      pageRender={pageRender}
                      bboxHint={activeBboxHint}
                      flipBBoxY={flipBBoxY}
                    />
                  )}
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button type="button" disabled={blockIndex <= 0} onClick={() => setBlockIndex(blockIndex - 1)}>
                      Prev block
                    </button>
                    <button type="button" disabled={blockIndex >= blocks.length - 1} onClick={() => setBlockIndex(blockIndex + 1)}>
                      Next block
                    </button>
                  </div>
                </>
              )}
            </div>

            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
              {currentBlock && (
                <>
                  <h3 style={{ marginTop: 0 }}>{currentBlock.label}</h3>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                    {currentBlock.role?.replace('_', ' ')} · {currentBlock.block_type}
                    {' · '}
                    Label signedness only (not whether the block was expected)
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <strong>KeyFlo (flash-lite):</strong>
                    <div>{currentBlock.keyflo_status || '—'}</div>
                    {currentBlock.keyflo_is_expected != null && (
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        is_expected={String(currentBlock.keyflo_is_expected)} (informational only)
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{currentBlock.keyflo_evidence || ''}</div>
                  </div>
                  {currentBlock.oracle_status && (
                    <div style={{ marginBottom: 12, background: currentBlock.disagreement ? '#fffbeb' : '#f9fafb', padding: 8, borderRadius: 4 }}>
                      <strong>Oracle (claude-sonnet-4-5):</strong>
                      <div>{currentBlock.oracle_status}</div>
                      {currentBlock.oracle_is_expected != null && (
                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                          is_expected={String(currentBlock.oracle_is_expected)} (informational only)
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{currentBlock.oracle_evidence || ''}</div>
                      {currentBlock.disagreement && <div style={{ color: '#d97706', fontSize: 12 }}>⚠ diff</div>}
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button
                      type="button"
                      disabled={
                        (currentBlock.keyflo_status !== 'signed' && currentBlock.keyflo_status !== 'unsigned')
                        || savingCase || loading || detectingBlocks
                      }
                      onClick={() => applyVerdict('confirm_keyflo')}
                    >
                      Confirm KeyFlo
                    </button>
                    {currentBlock.oracle_status && (
                      <button
                        type="button"
                        disabled={
                          (currentBlock.oracle_status !== 'signed' && currentBlock.oracle_status !== 'unsigned')
                          || savingCase || loading || detectingBlocks
                        }
                        onClick={() => applyVerdict('confirm_oracle')}
                      >
                        Confirm oracle
                      </button>
                    )}
                    <button type="button" disabled={savingCase || loading || detectingBlocks} onClick={() => applyVerdict('reject_unsigned')}>Reject unsigned (FN)</button>
                    <button type="button" disabled={savingCase || loading || detectingBlocks} onClick={() => applyVerdict('reject_signed')}>Reject signed (FP)</button>
                    <label style={{ fontSize: 13 }}>
                      <input
                        type="checkbox"
                        disabled={savingCase || loading || detectingBlocks}
                        checked={Boolean(currentBlock && missingBlockIds[currentBlock.block_id])}
                        onChange={(e) => {
                          if (!currentBlock) return;
                          setMissingBlockIds((prev) => ({
                            ...prev,
                            [currentBlock.block_id]: e.target.checked,
                          }));
                        }}
                      />
                      {' '}KeyFlo missed this block
                    </label>
                  </div>
                  {verdicts[currentBlock.block_id] && (
                    <div style={{ marginTop: 12, fontSize: 13, color: '#059669' }}>
                      Saved: {verdicts[currentBlock.block_id].status} ({verdicts[currentBlock.block_id].verdict_source})
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          <div style={{ padding: 40, color: '#6b7280' }}>
            {loading || detectingBlocks
              ? 'Loading case…'
              : (error
                ? 'Failed to load case. Select another case from the queue to retry.'
                : 'Select a case from the queue to begin auditing.')}
          </div>
        )}
      </div>
    </div>
  );
}

export default DeveloperSignatureEval;
